/**
 * Three weakest positions of the current squad (PRD-7.2, R02 Decision Record
 * on SE-8242). Pure: formation slots + roster in, ordered weaknesses out.
 *
 * Rules, in the DR's words:
 * 1. Rating = the roster player's existing decayed GPR, labelled "GPR".
 * 2. Baseline = first team only; a player is eligible for a slot when a
 *    natural position maps onto that slot's group (`POSITION_TO_SLOT_GROUP`).
 * 3. Starter = highest known rating, second = the next; each player counts
 *    once, placed where they rank best, ties by formation slot order. An
 *    unknown rating is excluded and counted as "n unrated" — never 0.
 * 4. Order: No cover (formation order) → lowest starter → larger drop-off
 *    ("No second choice" is the largest) → formation slot order.
 *
 * A slot whose only cover is unrated is neither "No cover" nor ranked with a
 * substituted 0: it is reported apart as not assessable.
 *
 * Rules 2–3 place the roster themselves, and only the scenario Pitch still
 * reads them (`computeThreeWeakest`). The watchlist Pitch ranks the cards'
 * own slot membership instead (`computeThreeWeakestFromSquad`, SE-8669): the
 * same page cannot show an 89 on the RB card and "GPR 60" for RB beside it.
 */
import { POSITION_TO_SLOT_GROUP } from "@/features/squad-planning/constants";
import type { TacticalSlot } from "@/generated/graphql";

/** Roster tier as the Pitch overlay reports it. */
export type WeakestRosterTier = "FIRST_TEAM" | "ACADEMY";

/** Minimal roster facts the computation needs. */
export interface WeakestRosterPlayer {
  readonly id: string;
  /** Natural position abbreviations (GK, LCB, CM, …). */
  readonly naturalPositions: readonly string[];
  readonly tier: WeakestRosterTier;
  /** Decayed GPR, or null when unknown. */
  readonly gpr: number | null;
}

/** Why a slot ranks where it does. */
export type WeaknessKind = "no-cover" | "worst-starter" | "drop-off";

/** One ranked weakness row. */
export interface SlotWeakness {
  readonly slot: TacticalSlot;
  readonly kind: WeaknessKind;
  readonly starter: number | null;
  readonly second: number | null;
  /** True when a starter exists and no second choice does. */
  readonly noSecondChoice: boolean;
  /** Eligible first-team players whose rating is unknown. */
  readonly unrated: number;
  /** Reason text, e.g. "Worst starter · GPR 68 · No second choice". */
  readonly label: string;
}

/** A slot covered only by unrated players. */
export interface UnassessableSlot {
  readonly slot: TacticalSlot;
  readonly unrated: number;
}

/** Computation result. */
export interface ThreeWeakestResult {
  /** Up to three rows in weakness order. */
  readonly weakest: readonly SlotWeakness[];
  /** Slots left out of the ranking because no eligible player is rated. */
  readonly unassessable: readonly UnassessableSlot[];
}

/** How many rows the widget shows. */
export const THREE_WEAKEST_COUNT = 3;

/**
 * Whether a player with these natural positions may cover the slot.
 * @param naturalPositions - Natural position abbreviations.
 * @param slot - Formation slot.
 * @returns True when a natural position equals the slot or maps onto it.
 */
export const isEligibleForSlot = (
  naturalPositions: readonly string[],
  slot: TacticalSlot
): boolean =>
  naturalPositions.some(
    position =>
      position === slot || POSITION_TO_SLOT_GROUP[position]?.includes(slot)
  );

/** Players placed per slot, rated and unrated apart. */
interface SlotCover {
  readonly rated: readonly number[];
  readonly unrated: number;
}

const EMPTY_COVER: SlotCover = { rated: [], unrated: 0 };

/**
 * Chooses the eligible slot where the player ranks best: the one holding the
 * fewest players so far, ties by formation slot order.
 * @param player - Roster player to place.
 * @param slots - Formation slots in order.
 * @param cover - Placements so far.
 * @returns The chosen slot, or undefined when no slot is eligible.
 */
const bestSlotFor = (
  player: WeakestRosterPlayer,
  slots: readonly TacticalSlot[],
  cover: ReadonlyMap<TacticalSlot, SlotCover>
): TacticalSlot | undefined =>
  slots
    .filter(slot => isEligibleForSlot(player.naturalPositions, slot))
    .reduce<TacticalSlot | undefined>((best, slot) => {
      if (best === undefined) return slot;
      const size = (entry: TacticalSlot) => {
        const placed = cover.get(entry) ?? EMPTY_COVER;
        return placed.rated.length + placed.unrated;
      };
      return size(slot) < size(best) ? slot : best;
    }, undefined);

/**
 * Places every first-team player once: rated players from the highest rating
 * down, then unrated players, each into the slot where they rank best.
 * @param slots - Formation slots in order.
 * @param roster - Complete roster.
 * @returns Cover per slot.
 */
const placeRoster = (
  slots: readonly TacticalSlot[],
  roster: readonly WeakestRosterPlayer[]
): ReadonlyMap<TacticalSlot, SlotCover> => {
  const firstTeam = roster.filter(player => player.tier === "FIRST_TEAM");
  const rated = firstTeam
    .filter(player => typeof player.gpr === "number")
    .sort((left, right) => (right.gpr ?? 0) - (left.gpr ?? 0));
  const unrated = firstTeam.filter(player => typeof player.gpr !== "number");
  return [...rated, ...unrated].reduce((cover, player) => {
    const slot = bestSlotFor(player, slots, cover);
    if (slot === undefined) return cover;
    const placed = cover.get(slot) ?? EMPTY_COVER;
    const next =
      typeof player.gpr === "number"
        ? { ...placed, rated: [...placed.rated, player.gpr] }
        : { ...placed, unrated: placed.unrated + 1 };
    return new Map(cover).set(slot, next);
  }, new Map<TacticalSlot, SlotCover>());
};

/** Per-slot players as a pitch card holds them; only the rating is read. */
export type SquadBySlotCover = Partial<
  Record<TacticalSlot, readonly { readonly gpr?: number | null }[]>
>;

/**
 * The cards' slot membership as cover (SE-8669): every player a card shows,
 * whatever his tier, ratings highest first. A 0 is a rating.
 * @param squadBySlot - Squad players per slot, as the cards draw them
 * @returns Cover per slot.
 */
const coverFromSquadBySlot = (
  squadBySlot: SquadBySlotCover
): ReadonlyMap<TacticalSlot, SlotCover> =>
  new Map(
    (
      Object.entries(squadBySlot) as [
        TacticalSlot,
        SquadBySlotCover[TacticalSlot],
      ][]
    ).map(([slot, players = []]) => {
      const ratings = players.flatMap(player =>
        typeof player.gpr === "number" ? [player.gpr] : []
      );
      return [
        slot,
        {
          rated: [...ratings].sort((left, right) => right - left),
          unrated: players.length - ratings.length,
        },
      ];
    })
  );

/** A slot with a known starter, before ranking. */
interface AssessableSlot {
  readonly slot: TacticalSlot;
  readonly starter: number;
  readonly second: number | null;
  readonly unrated: number;
  readonly order: number;
}

/**
 * Larger drop-off ranks weaker; no second choice is the largest drop-off.
 * @param entry - Assessable slot.
 * @returns Drop-off used for ordering.
 */
const dropOff = (entry: AssessableSlot): number =>
  entry.second === null
    ? Number.POSITIVE_INFINITY
    : entry.starter - entry.second;

/**
 * Orders assessable slots: lowest starter, larger drop-off, formation order.
 * @param left - One slot.
 * @param right - Another slot.
 * @returns Sort comparator result.
 */
const byWeakness = (left: AssessableSlot, right: AssessableSlot): number =>
  left.starter - right.starter ||
  dropOff(right) - dropOff(left) ||
  left.order - right.order;

/**
 * Formats a rating the way the Pitch does elsewhere.
 * @param rating - Raw rating.
 * @returns Rounded integer text.
 */
const formatRating = (rating: number): string => Math.round(rating).toString();

/**
 * Builds the row for an assessable slot; the first slot of a tie on the
 * starter rating owes its place to the drop-off and says so.
 * @param entry - Assessable slot.
 * @param leadsTie - Whether the next slot shares this starter rating.
 * @returns Weakness row.
 */
const toWeakness = (entry: AssessableSlot, leadsTie: boolean): SlotWeakness => {
  const noSecondChoice = entry.second === null;
  const kind: WeaknessKind =
    leadsTie && entry.second !== null ? "drop-off" : "worst-starter";
  const label =
    kind === "drop-off"
      ? `Biggest drop-off · ${formatRating(entry.starter)} → ${formatRating(entry.second as number)}`
      : `Worst starter · GPR ${formatRating(entry.starter)}${noSecondChoice ? " · No second choice" : ""}`;
  return {
    slot: entry.slot,
    kind,
    starter: entry.starter,
    second: entry.second,
    noSecondChoice,
    unrated: entry.unrated,
    label,
  };
};

/**
 * Computes the three weakest slots of a formation for the current roster.
 * @param slots - Formation slots in formation order (`PITCH_FORMATION_ROWS` flattened).
 * @param roster - Complete current roster, every tier.
 * @param formationLabel - The formation as the toolbar shows it ("4-3-3");
 * names the empty slot's formation on the No cover row (SE-8414).
 * @returns Ordered weaknesses and the slots that cannot be assessed.
 */
export const computeThreeWeakest = (
  slots: readonly TacticalSlot[],
  roster: readonly WeakestRosterPlayer[],
  formationLabel?: string
): ThreeWeakestResult =>
  rankSlotCover(slots, placeRoster(slots, roster), formationLabel);

/**
 * Computes the three weakest slots from the pitch cards' own membership
 * (SE-8669): starter = the card's highest rating, second = the next.
 * @param slots - Formation slots in formation order
 * @param squadBySlot - Squad players per slot, as the cards draw them
 * @param formationLabel - The formation as the toolbar shows it ("4-3-3")
 * @returns Ordered weaknesses and the slots that cannot be assessed.
 */
export const computeThreeWeakestFromSquad = (
  slots: readonly TacticalSlot[],
  squadBySlot: SquadBySlotCover,
  formationLabel?: string
): ThreeWeakestResult =>
  rankSlotCover(slots, coverFromSquadBySlot(squadBySlot), formationLabel);

/**
 * Ranks placed cover: No cover (formation order), then assessable slots by
 * lowest starter, larger drop-off, formation order.
 * @param slots - Formation slots in formation order
 * @param cover - Cover per slot, ratings highest first
 * @param formationLabel - Names the empty slot's formation on No cover rows
 * @returns Ordered weaknesses and the slots that cannot be assessed.
 */
const rankSlotCover = (
  slots: readonly TacticalSlot[],
  cover: ReadonlyMap<TacticalSlot, SlotCover>,
  formationLabel?: string
): ThreeWeakestResult => {
  const noCoverLabel = formationLabel
    ? `No cover · empty slot in ${formationLabel}`
    : "No cover";
  const noCover = slots
    .filter(slot => {
      const placed = cover.get(slot) ?? EMPTY_COVER;
      return placed.rated.length === 0 && placed.unrated === 0;
    })
    .map<SlotWeakness>(slot => ({
      slot,
      kind: "no-cover",
      starter: null,
      second: null,
      noSecondChoice: false,
      unrated: 0,
      label: noCoverLabel,
    }));
  const unassessable = slots.flatMap<UnassessableSlot>(slot => {
    const placed = cover.get(slot) ?? EMPTY_COVER;
    return placed.rated.length === 0 && placed.unrated > 0
      ? [{ slot, unrated: placed.unrated }]
      : [];
  });
  const assessable = slots.flatMap<AssessableSlot>((slot, order) => {
    const placed = cover.get(slot) ?? EMPTY_COVER;
    const [starter, second] = placed.rated;
    return starter === undefined
      ? []
      : [
          {
            slot,
            starter,
            second: second ?? null,
            unrated: placed.unrated,
            order,
          },
        ];
  });
  const ranked = [...assessable].sort(byWeakness).map((entry, index, all) => {
    const previous = all[index - 1];
    const next = all[index + 1];
    const leadsTie =
      next !== undefined &&
      next.starter === entry.starter &&
      (previous === undefined || previous.starter !== entry.starter);
    return toWeakness(entry, leadsTie);
  });
  return {
    weakest: [...noCover, ...ranked].slice(0, THREE_WEAKEST_COUNT),
    unassessable,
  };
};

/**
 * The three weakest slots as a set — what the pitch cards read to draw their
 * `weakest` (dashed) border (SE-8526, Figma 1010:20306). Same ranking as the
 * THREE WEAKEST POSITIONS panel, so the card and the panel never disagree.
 * @param slots - The formation's tactical slots
 * @param roster - The ● current squad
 * @returns The weakest slots (at most three).
 */
export const weakestSlotSet = (
  slots: readonly TacticalSlot[],
  roster: readonly WeakestRosterPlayer[]
): ReadonlySet<TacticalSlot> =>
  new Set(computeThreeWeakest(slots, roster).weakest.map(item => item.slot));

/**
 * The watchlist Pitch's three weakest slots as a set, from the cards' own
 * membership — the dashed border and the widget read one ranking (SE-8669).
 * @param slots - The formation's tactical slots
 * @param squadBySlot - Squad players per slot, as the cards draw them
 * @returns The weakest slots (at most three).
 */
export const weakestSlotSetFromSquad = (
  slots: readonly TacticalSlot[],
  squadBySlot: SquadBySlotCover
): ReadonlySet<TacticalSlot> =>
  new Set(
    computeThreeWeakestFromSquad(slots, squadBySlot).weakest.map(
      item => item.slot
    )
  );
